import { getB2bConfig, listB2bCustomers } from '@api/b2b';
import PageHeader from '@components/common/PageHeader';
import { SheetTabStrip } from '@components/ui/DataSheet';
import FilterDropdown from '@components/ui/Dropdown';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { Button } from '@mantine/core';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import styles from './b2b.module.css';
import PriceListEditor from './PriceListEditor';
import SchemesSection from './SchemesSection';
import { priceFallbacks } from './utils';

/**
 * Price lists and schemes, one tab each. A price resolves customer list → segment list → default
 * list, per SKU; schemes add free goods or discounts on top, by slab, for all customers or for
 * chosen segments, tags or customers.
 *
 * Query: `tab=schemes` opens the Schemes tab.
 */
const PricingPage: React.FC = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const { currentUser } = useCurrentUser();
    const { data: config } = useQuery({ queryKey: ['b2b-config'], queryFn: getB2bConfig });
    const { data: customers = [] } = useQuery({ queryKey: ['b2b-customers'], queryFn: listB2bCustomers });
    const [owner, setOwner] = useState('DEFAULT');
    const tab = params.get('tab') === 'schemes' ? 'schemes' : 'prices';

    const options = [
        { label: 'Default price list', value: 'DEFAULT' },
        ...(config?.segments ?? []).map((s) => ({ label: `Segment: ${s.label}`, value: `SEGMENT:${s.key}` })),
        ...customers.map((c) => ({ label: `Customer: ${c.name}`, value: `CUSTOMER:${c.customerId}` })),
    ];
    const label = options.find((o) => o.value === owner)?.label ?? owner;
    const ownerCustomer = customers.find((c) => owner === `CUSTOMER:${c.customerId}`);

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <div className={styles.between}>
                <PageHeader icon="🏷️" title="Price lists & schemes" subtitle="What each B2B customer pays, and the schemes that apply on top" />
                <Button variant={ButtonVariant.DEFAULT} leftSection={<ArrowLeftIcon size={16} />} onClick={() => void navigate('/b2b/customers')}>
                    B2B customers
                </Button>
            </div>
            <div className={`${styles.sheet} ${styles.noShrink}`}>
                <SheetTabStrip
                    tabs={[
                        { id: 'prices', label: 'Price lists' },
                        { id: 'schemes', label: 'Schemes' },
                    ]}
                    activeTab={tab}
                    onTabChange={(id) => setParams(id === 'schemes' ? { tab: 'schemes' } : {}, { replace: true })}
                />
            </div>
            {tab === 'prices' ? (
                <>
                    <div className={styles.row}>
                        <FilterDropdown value={owner} onChange={setOwner} options={options} placeholder="Price list" />
                        <span className={styles.muted}>A customer pays its own price if set, else its segment's, else the default. On a segment or customer list, switching Offered off stops selling that product to them.</span>
                    </div>
                    <PriceListEditor
                        key={owner}
                        owner={owner}
                        title={label}
                        fallbacks={priceFallbacks(owner, ownerCustomer ? { key: ownerCustomer.segment, label: ownerCustomer.segmentLabel } : null)}
                    />
                </>
            ) : (
                <SchemesSection config={config} customers={customers} isAdmin={currentUser?.role === 'ADMIN'} />
            )}
        </div>
    );
};

export default PricingPage;
